/** A4 geometry used by wallet PDF export, matching Puppeteer 12mm/10mm margins at 96dpi. */
export const A4_WIDTH_MM = 210;
export const A4_HEIGHT_MM = 297;
export const MARGIN_TOP_MM = 12;
export const MARGIN_RIGHT_MM = 10;
export const MARGIN_BOTTOM_MM = 12;
export const MARGIN_LEFT_MM = 10;

export const A4_WIDTH_PX = Math.round((A4_WIDTH_MM / 25.4) * 96);
export const A4_HEIGHT_PX = Math.round((A4_HEIGHT_MM / 25.4) * 96);
export const MARGIN_TOP_PX = Math.round((MARGIN_TOP_MM / 25.4) * 96);
export const MARGIN_RIGHT_PX = Math.round((MARGIN_RIGHT_MM / 25.4) * 96);
export const MARGIN_BOTTOM_PX = Math.round((MARGIN_BOTTOM_MM / 25.4) * 96);
export const MARGIN_LEFT_PX = Math.round((MARGIN_LEFT_MM / 25.4) * 96);

export const PDF_PAGE_CLASS = 'pdf-page';
const OVERFLOW_TOLERANCE_PX = 2;

function isHtmlElement(node: Node | null): node is HTMLElement {
  return !!node && node.nodeType === 1;
}

export function pageOverflows(page: HTMLElement): boolean {
  void page.offsetHeight;
  return page.scrollHeight > page.clientHeight + OVERFLOW_TOLERANCE_PX;
}

export function createPdfPage(doc: Document): HTMLElement {
  const page = doc.createElement('div');
  page.className = PDF_PAGE_CLASS;
  page.style.cssText = [
    `width:${A4_WIDTH_PX}px`,
    `min-height:${A4_HEIGHT_PX}px`,
    `height:${A4_HEIGHT_PX}px`,
    `max-height:${A4_HEIGHT_PX}px`,
    `padding:${MARGIN_TOP_PX}px ${MARGIN_RIGHT_PX}px ${MARGIN_BOTTOM_PX}px ${MARGIN_LEFT_PX}px`,
    'box-sizing:border-box',
    'background:#ffffff',
    'overflow:hidden',
    'position:relative',
    'margin:0',
    "font-family:'Helvetica Neue',Arial,sans-serif",
    'color:#111827',
    'font-size:14px',
  ].join(';');
  return page;
}

function tableBodyRows(table: HTMLTableElement): HTMLTableRowElement[] {
  const fromBody = Array.from(table.tBodies[0]?.rows ?? []);
  if (fromBody.length) {
    return fromBody;
  }
  return Array.from(table.querySelectorAll('tr')).filter(
    (row) => !row.closest('thead'),
  );
}

function isHeadingLike(el: HTMLElement | null): boolean {
  if (!el) {
    return false;
  }
  return el.tagName === 'H2' || el.classList.contains('note');
}

function pullTrailingHeadings(page: HTMLElement): HTMLElement[] {
  const moved: HTMLElement[] = [];
  while (isHeadingLike(isHtmlElement(page.lastElementChild) ? page.lastElementChild : null)) {
    const last = page.lastElementChild as HTMLElement;
    page.removeChild(last);
    moved.unshift(last);
  }
  return moved;
}

function makeContinuationTable(
  source: HTMLTableElement,
  doc: Document,
): HTMLTableElement {
  const next = source.cloneNode(false) as HTMLTableElement;
  const thead = source.querySelector('thead');
  if (thead) {
    next.appendChild(thead.cloneNode(true));
  }
  next.appendChild(doc.createElement('tbody'));
  return next;
}

function tbodyOf(table: HTMLTableElement, doc: Document): HTMLTableSectionElement {
  return table.tBodies[0] || table.appendChild(doc.createElement('tbody'));
}

/**
 * Move report nodes into fixed A4 page boxes. A block or table row is appended
 * only when `page.scrollHeight <= page.clientHeight`; otherwise it starts a new
 * page. Table headers are repeated on continuation pages.
 */
export function paginateReportIntoPages(body: HTMLElement): HTMLElement[] {
  const doc = body.ownerDocument;
  const children = Array.from(body.childNodes).filter(isHtmlElement);
  if (!children.length) {
    throw new Error('Wallet report HTML was empty before pagination');
  }
  body.style.margin = '0';
  body.style.padding = '0';
  body.style.background = '#ffffff';
  body.replaceChildren();

  const pages: HTMLElement[] = [];
  const newPage = (): HTMLElement => {
    const page = createPdfPage(doc);
    body.appendChild(page);
    pages.push(page);
    return page;
  };

  let current = newPage();

  const startFreshPage = (carry: HTMLElement[] = []): HTMLElement => {
    current = newPage();
    for (const el of carry) {
      current.appendChild(el);
    }
    return current;
  };

  const appendFitting = (el: HTMLElement): void => {
    current.appendChild(el);
    if (!pageOverflows(current)) {
      return;
    }
    current.removeChild(el);
    if (!current.childElementCount) {
      current.appendChild(el);
      return;
    }
    const headings = isHeadingLike(el) ? [] : pullTrailingHeadings(current);
    startFreshPage(headings).appendChild(el);
  };

  const appendNarrative = (narrative: HTMLElement): void => {
    const paras = Array.from(narrative.childNodes).filter(isHtmlElement);
    if (!paras.length) {
      appendFitting(narrative);
      return;
    }

    const makeBox = (): HTMLElement =>
      narrative.cloneNode(false) as HTMLElement;

    let box = makeBox();
    current.appendChild(box);
    if (pageOverflows(current) && current.childElementCount > 1) {
      current.removeChild(box);
      const headings = pullTrailingHeadings(current);
      startFreshPage(headings).appendChild(box);
    }

    for (const para of paras) {
      box.appendChild(para);
      if (!pageOverflows(current)) {
        continue;
      }
      box.removeChild(para);
      if (!box.childElementCount) {
        current.removeChild(box);
        const headings = pullTrailingHeadings(current);
        startFreshPage(headings);
        box = makeBox();
        current.appendChild(box);
        box.appendChild(para);
        continue;
      }
      startFreshPage();
      box = makeBox();
      current.appendChild(box);
      box.appendChild(para);
    }

    if (!box.childElementCount) {
      box.remove();
    }
  };

  const appendTable = (table: HTMLTableElement): void => {
    current.appendChild(table);
    if (!pageOverflows(current)) {
      return;
    }
    current.removeChild(table);

    const rows = tableBodyRows(table);
    let partial = makeContinuationTable(table, doc);
    current.appendChild(partial);

    if (pageOverflows(current) && current.childElementCount > 1) {
      current.removeChild(partial);
      const headings = pullTrailingHeadings(current);
      startFreshPage(headings).appendChild(partial);
    }

    for (const row of rows) {
      const tbody = tbodyOf(partial, doc);
      tbody.appendChild(row);
      if (!pageOverflows(current)) {
        continue;
      }

      tbody.removeChild(row);
      if (tbody.rows.length === 0) {
        current.removeChild(partial);
        const headings = pullTrailingHeadings(current);
        startFreshPage(headings);
        partial = makeContinuationTable(table, doc);
        current.appendChild(partial);
        tbodyOf(partial, doc).appendChild(row);
        continue;
      }

      startFreshPage();
      partial = makeContinuationTable(table, doc);
      current.appendChild(partial);
      tbodyOf(partial, doc).appendChild(row);
      if (pageOverflows(current) && current.childElementCount > 1) {
        tbodyOf(partial, doc).removeChild(row);
        current.removeChild(partial);
        startFreshPage();
        current.appendChild(partial);
        tbodyOf(partial, doc).appendChild(row);
      }
    }

    if (tbodyOf(partial, doc).rows.length === 0) {
      partial.remove();
    }
  };

  for (const child of children) {
    if (child.tagName === 'TABLE') {
      appendTable(child as HTMLTableElement);
    } else if (child.classList.contains('narrative')) {
      appendNarrative(child);
    } else {
      appendFitting(child);
    }
  }

  while (pages.length > 1) {
    const last = pages[pages.length - 1];
    if (last.childElementCount > 0) {
      break;
    }
    last.remove();
    pages.pop();
  }

  return pages;
}

export function findClippedTableRows(pages: HTMLElement[]): HTMLElement[] {
  const clipped: HTMLElement[] = [];
  for (const page of pages) {
    const pageRect = page.getBoundingClientRect();
    const view = page.ownerDocument.defaultView;
    const style = view?.getComputedStyle(page);
    const padTop = parseFloat(style?.paddingTop || '0') || 0;
    const padBottom = parseFloat(style?.paddingBottom || '0') || 0;
    const contentTop = pageRect.top + padTop;
    const contentBottom = pageRect.bottom - padBottom;
    for (const row of Array.from(page.querySelectorAll('tr'))) {
      const rect = row.getBoundingClientRect();
      if (rect.height < 1) {
        continue;
      }
      if (rect.top < contentTop - 1 || rect.bottom > contentBottom + 1) {
        clipped.push(row as HTMLElement);
      }
    }
  }
  return clipped;
}
